import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import { SentenceDifficultyTone } from './utils';

export const GalleryWrapper = styled.div<{ $overlay?: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
    margin-top: ${theme.sz.s4};

    ${
      $overlay &&
      css`
        [data-testid='example-sentences-title'] {
          ${theme.typography.paragraph.s};
          color: ${theme.co.neutral90};
          text-transform: none;
          letter-spacing: normal;
        }

        [data-testid='example-sentences-view-all'],
        [data-testid='example-sentences-toggle-difficulty'] {
          color: ${theme.co.neutral90};

          &:hover {
            color: ${theme.co.neutral99};
          }
        }

        [data-testid='example-sentences-third-party-info'] button {
          color: ${theme.co.neutral90};

          &:hover {
            color: ${theme.co.neutral99};
          }
        }
      `
    }
  `,
);

export const TitleRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s2};
  `,
);

export const GalleryTitle = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxxs};
    color: ${theme.co.neutral40};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  `,
);

export const TitleIconButton = styled.button(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    padding: 0;
    border: none;
    border-radius: ${theme.bo.small};
    background: none;
    color: ${theme.co.neutral40};
    font-size: ${theme.sz.s4};
    cursor: pointer;

    &:hover {
      color: ${theme.co.neutral20};
    }

    &:focus-visible {
      outline: ${theme.bo.mediumWidth} solid ${theme.co.success40};
      outline-offset: ${theme.sz.s1};
    }
  `,
);

export const DifficultyToggleButton = styled(TitleIconButton)`
  margin-left: auto;
`;

export const TitleInfoIcon = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    flex-shrink: 0;

    button {
      margin-left: 0;
      font-size: ${theme.sz.s4};
      color: ${theme.co.neutral40};
    }

    button:hover {
      color: ${theme.co.neutral20};
    }
  `,
);

export const ScrollContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
    overflow-y: auto;
    /* Place the vertical scrollbar on the left. */
    direction: rtl;
    align-items: flex-start;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: ${theme.co.neutral95};
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.co.neutral80};
      border-radius: 2px;
    }

    & > * {
      direction: ltr;
    }
  `,
);

export const SentenceCard = styled.div(
  ({ theme }) => css`
    background-color: ${theme.co.neutral99};
    border: 1px solid ${theme.co.neutral90};
    border-radius: ${theme.sz.s2};
    padding: ${theme.sz.s2} ${theme.sz.s3};
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${theme.sz.s2};
    width: fit-content;
    max-width: 100%;
    box-sizing: border-box;
    height: fit-content;
  `,
);

export const AllExamplesList = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
    width: 100%;
    box-sizing: border-box;
    max-height: 70vh;
    overflow-y: auto;
    padding: ${theme.sz.s3} ${theme.sz.s14} ${theme.sz.s4} ${theme.sz.s4};
    align-items: stretch;

    ${SentenceCard} {
      width: 100%;
    }
  `,
);

export const CardContent = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s1};
    flex: 1;
    min-width: 0;
  `,
);

export const TargetRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.sz.s2};
  `,
);

// Same bands/colors as CAS's DifficultyScorePill (DifficultyScorePill.module.css),
// so a sentence's perceived difficulty reads the same in both tools.
const DIFFICULTY_TONE_STYLES: Record<SentenceDifficultyTone, ReturnType<typeof css>> = {
  easy: css`
    color: #116329;
    background-color: #dafbe1;
    border-color: #4ac26b66;
  `,
  mid: css`
    color: #9a6700;
    background-color: #fff8c5;
    border-color: #d4a72c66;
  `,
  hard: css`
    color: #953800;
    background-color: #fff1e5;
    border-color: #fb8f4466;
  `,
  expert: css`
    color: #cf222e;
    background-color: #ffebe9;
    border-color: #ff818266;
  `,
};

export const DifficultyPill = styled.span<{ $tone?: SentenceDifficultyTone }>(
  ({ theme, $tone }) => css`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    box-sizing: border-box;
    margin-left: auto;
    ${theme.typography.paragraph.s};
    height: 1.5em;
    padding: 0 ${theme.sz.s2};
    border-radius: 999px;
    border: 1px solid ${theme.co.neutral80};
    background-color: ${theme.co.neutral95};
    color: ${theme.co.neutral20};
    font-variant-numeric: tabular-nums;
    line-height: 1;
    white-space: nowrap;

    ${$tone && DIFFICULTY_TONE_STYLES[$tone]}
  `,
);

export const ModalTitleRow = styled(TitleRow)(
  ({ theme }) => css`
    min-height: ${theme.sz.s8};
    margin-bottom: ${theme.sz.s1};
  `,
);

export const SortToggle = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: ${theme.sz.s05};
    padding: ${theme.sz.s05};
    border-radius: ${theme.bo.round};
    background: ${transparentize(0.25, theme.co.neutral95)};
    backdrop-filter: blur(${theme.sz.s4});
    border: ${theme.bo.thinWidth} solid ${transparentize(0.55, theme.co.neutral80)};
    box-shadow: 0 ${theme.sz.s05} ${theme.sz.s2} ${transparentize(0.88, theme.co.neutral10)};
  `,
);

export const SortOption = styled.button<{ $isSelected: boolean }>(
  ({ theme, $isSelected }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s05};
    min-width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    padding: 0 ${theme.sz.s2};
    border: none;
    border-radius: ${theme.bo.round};
    background: ${$isSelected ? theme.co.neutral99 : 'transparent'};
    color: ${$isSelected ? theme.co.neutral10 : theme.co.neutral40};
    box-shadow: ${
      $isSelected
        ? `0 ${theme.sz.s0} ${theme.sz.s1} ${transparentize(0.86, theme.co.neutral10)}`
        : 'none'
    };
    font-size: ${theme.sz.s3};
    cursor: pointer;

    &:hover {
      color: ${theme.co.neutral10};
    }

    &:focus-visible {
      outline: ${theme.bo.mediumWidth} solid ${theme.co.success40};
      outline-offset: ${theme.sz.s1};
    }
  `,
);

export const SortDirectionIcon = styled.span(
  ({ theme }) => css`
    display: inline-flex;
    font-size: ${theme.sz.s2};
  `,
);

export const HeaderActions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s1};
    flex-shrink: 0;
    margin-left: auto;
  `,
);

export const AttributionIcon = styled.div(
  ({ theme }) => css`
    flex-shrink: 0;
    opacity: 0.5;

    &:hover,
    &:focus-within {
      opacity: 1;
    }

    button {
      margin-left: 0;
      font-size: ${theme.sz.s4};
      color: ${theme.co.neutral60};
    }
  `,
);

export const TargetValue = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.neutral10};
    margin: 0;
    max-width: none;
    white-space: pre-line;
    flex: 1;
    min-width: 0;
  `,
);

export const LearnableHighlight = styled.span(
  ({ theme }) => css`
    text-decoration: underline;
    text-decoration-thickness: ${theme.bo.mediumWidth};
    text-underline-offset: ${theme.sz.s1};
    text-decoration-color: ${theme.co.secondary40};
  `,
);

export const SourceValue = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral40};
    margin: 0;
    max-width: none;
    white-space: pre-line;
  `,
);

export const RomanizationValue = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral50};
    margin: 0;
    max-width: none;
    font-style: italic;
    white-space: pre-line;
  `,
);

export const AudioContainer = styled.div(
  ({ theme }) => css`
    display: flex;

    button,
    [data-testid='pulse'] {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }

    button {
      padding: 0;
    }

    svg {
      width: ${theme.sz.s4};
      height: ${theme.sz.s4};
    }
  `,
);
